/**
 * Phase2Panel.tsx
 * FEAT-2026-0007 Phase 6C.1 — Phase-2 (Employee planning) body.
 *
 * Renders:
 *   1. "Project Testing Grid (Define Plan)" — same tasks as Phase 1 but machine
 *      columns are read-only; an "Assigned Employees (Skill Level)" column is
 *      appended (reuses InlineAssigneePicker).
 *
 * Phases 6C.2–6C.6 (weekly allocation grid, capacity table, compare overlay)
 * are NOT in this item and remain as a clearly-marked placeholder at the bottom.
 *
 * Key wiring:
 *   - Tasks loaded via listTasksForSource(source, sourceKey, scenarioKey).
 *   - Per-task assignees owned by useTaskAssignments — SINGLE SOURCE OF TRUTH
 *     shared by both the top grid (Phase2TaskGrid) and the bottom weekly grid
 *     (Phase2WeeklyGrid). Phase 8.1 fix for REQ-07 desync.
 *   - InlineAssigneePicker / AssignEmployeesDialog receive scenarioKey; every
 *     assignment is a presence (Wave 3 — server pins Percent=100).
 *   - 409 skill_gate goes through the existing MentorRequiredModal flow;
 *     409 absence_day_conflict is a hard error shown verbatim (Q3B).
 *
 * Split: heavy grid rendering is in Phase2TaskGrid.tsx (≤600-line compliance).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import GroupsIcon from '@mui/icons-material/Groups';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { RAIL_WEEK_W } from './phase2RailGeometry';
import { Phase2RailHeader } from './Phase2RailHeader';
import { Phase2RailScrollArrows } from './Phase2RailScrollArrows';
import { weekStartsInRange } from '../../../../utils/weekUtils';

import { Phase2TaskGrid } from './Phase2TaskGrid';
import { Phase2WeeklyGrid } from './Phase2WeeklyGrid';
import { Phase2BoardGrid } from './Phase2BoardGrid';
import { StaffThisJobView } from './StaffThisJobView';
import { StaffRosterView } from './StaffRosterView';
import { type Phase2ViewMode } from './phase2ViewMode';
import { useScheduleHolidays } from '../../../../hooks/useScheduleHolidays';
import { Phase2CapacityTable } from './Phase2CapacityTable';
import { useFlashUserRow } from './useFlashUserRow';
import { FlashUserContext } from './flashUserContext';
import { Phase2BulkActionBar } from './ScenarioBulkActionBar';
import { AssignEmployeesDialog } from '../../tasks/AssignEmployeesDialog';
import { IndexStaffingSection } from './index/IndexStaffingSection';
import {
  listTasksForSource,
  type TaskWithBookings,
} from '../../../../services/scheduleTasks';
import type { AssignableUser } from '../../../../services/scheduleAssignments';
import type { Scenario } from '../../../../services/scheduleScenario';
import { useTaskAssignments } from './useTaskAssignments';
import { onScheduleChange, emitScheduleChange } from '../../../../services/scheduleBus';
import { clearCapacityCache } from '../../../../components/schedule/assigneeOption';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Phase2PanelProps {
  /** Whether Compare to Baseline overlay is active (used by future 6C.6 grids). */
  compareToBaseline: boolean;
  /** Phase-3 view — CONTROLLED by ScenarioPhaseStepper since 2026-07-26 (the
   *  switcher lives on the phase-tab row); persistence stays in phase2ViewMode. */
  viewMode: Phase2ViewMode;
  onViewModeChange: (v: Phase2ViewMode) => void;
  /** Active scenario — provides ScenarioKey, SourceType, QuoteKey / WorkOrderKey. */
  scenario: Scenario | null;
  /**
   * 6D.2 — On-enter refresh key. Incremented by ScenarioPhaseStepper whenever
   * the user enters Phase 2 (advance from Phase 1). Triggers a fresh data load
   * so Phase-1 machine/date edits are visible in the employee task grid and the
   * capacity table — without discarding open pickers or scroll position.
   */
  onEnterRefreshKey?: number;
  /**
   * Index-only fix (2026-08-04): true when the source has pooled allocations
   * (index or effort kind — the stepper's any-kind count). The "Staff this job"
   * hero renders its allocation rows even with ZERO machine tasks, so its gate
   * (and the empty-state copy) key off this instead of tasks alone.
   */
  hasIndexTests?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Phase2Panel: React.FC<Phase2PanelProps> = ({
  compareToBaseline,
  viewMode,
  onViewModeChange,
  scenario,
  onEnterRefreshKey = 0,
  hasIndexTests = false,
}) => {
  const theme = useTheme();

  // Derive source info from the scenario
  const source: 'Quote' | 'WorkOrder' | null = scenario?.SourceType ?? null;
  const sourceKey: string | null =
    source === 'Quote' ? (scenario?.QuoteKey ?? null) : (scenario?.WorkOrderKey ?? null);
  const scenarioKey: string | null = scenario?.ScenarioKey ?? null;

  // ── State ────────────────────────────────────────────────────────────────

  const [tasks, setTasks] = useState<TaskWithBookings[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stat holidays across the tasks' span — so the Duration column counts working
  // days only (matches Phase 1 + the Machine Schedule grid shading; NB default).
  const holWindow = useMemo(() => {
    const starts = tasks.map((t) => t.StartDate).filter((s): s is string => Boolean(s)).sort();
    const ends = tasks.map((t) => t.EndDate).filter((s): s is string => Boolean(s)).sort();
    const today = new Date().toISOString().slice(0, 10);
    const rawEnd = ends[ends.length - 1] ?? today;
    // The capacity table renders whole BST weeks, so the last week's Friday can sit
    // up to 6 days past the last task end — pad the holiday window so a holiday in
    // that trailing week is still shaded as non-working.
    const [ey, em, ed] = rawEnd.split('-').map(Number);
    const paddedEnd = new Date(Date.UTC(ey, em - 1, ed + 7)).toISOString().slice(0, 10);
    return { start: starts[0] ?? today, end: paddedEnd };
  }, [tasks]);
  const { holidays: holidayRows } = useScheduleHolidays(holWindow.start, holWindow.end);
  const holidayDates = useMemo(() => holidayRows.map((h) => h.date.slice(0, 10)), [holidayRows]);

  /**
   * Phase 8.1 — Single source of truth for EmployeeAssignment data (REQ-07).
   * Both the top task grid (Phase2TaskGrid) and the bottom weekly grid
   * (Phase2WeeklyGrid) receive assignmentMap from this hook.
   * reloadOne is passed down as onReloadAssignees / onAssignmentsChanged so
   * any mutation in either panel invalidates the shared state once.
   */
  const { assignmentMap, reloadOne: reloadOneAssignment } = useTaskAssignments(
    tasks,
    scenarioKey ?? undefined,
  );

  /** Task currently open in AssignEmployeesDialog (escalation from picker). */
  const [dialogTask, setDialogTask] = useState<TaskWithBookings | null>(null);
  /** User pre-selected in the dialog (SkillGate / Overbook escalation). */
  const [dialogPreselect, setDialogPreselect] = useState<AssignableUser | null>(null);

  // ── 6F.4 — Multi-select (matches the mockup's Multi-select toggle) ──────────
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  // Shared rail "beam" (2026-06-23): the week (Saturday ISO) hovered in EITHER the task
  // grid or the capacity grid, so both light up the same week column at once.
  const [hoveredWeek, setHoveredWeek] = useState<string | null>(null);

  // Employee-planning presentation — per-user, remembered in localStorage.
  // Defaults to the "Staff this job" hero (PHASE2-REFRAME.md); Classic / Board /
  // Roster are the legacy views, one click away. The storage key is bumped so
  // every existing user lands in Staff once, then their own choice persists.
  // Supervisor-rule tip banner — dismissible, remembered (designer pass 2026-07-26).
  const [tipDismissed, setTipDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem('scenario.staffTip.dismissed') === '1'; } catch { return false; }
  });
  const dismissTip = useCallback(() => {
    setTipDismissed(true);
    try { localStorage.setItem('scenario.staffTip.dismissed', '1'); } catch { /* private mode */ }
  }, []);
  // Staff + Roster are full-width standalone hero surfaces (own layout, no rail);
  // Classic + Board are the legacy rail views that also show the read-only
  // Project Testing Grid below.
  const showLegacyRail = viewMode === 'classic' || viewMode === 'board';
  // The Staff hero staffs pooled index/effort allocations INSIDE "Tests on this
  // job", so it must render for an index-only plan (zero machine tasks) too —
  // gating it on tasks alone dead-ended those plans on "No tasks found"
  // (index-only fix, 2026-08-04).
  const showStaffHero = viewMode === 'staff' && (tasks.length > 0 || hasIndexTests);

  // Floating left/right scroll arrows for the shared rail (2026-06-23): the single
  // scrollbar sits at the bottom of a tall rail, so these always-visible edge buttons
  // let you page the weeks sideways from anywhere. Shown only when there's room to scroll.
  const railScrollRef = useRef<HTMLDivElement>(null);
  // Click a person's name/avatar in any task view → flash their row in the capacity grid
  // below (the rail wraps both, so a query within it locates the row). 2026-06-25.
  const flashUserRow = useFlashUserRow(railScrollRef);
  const handleFlashUser = useCallback(
    (userKey: string) => flashUserRow(userKey, theme.schedule.employeeAlloc),
    [flashUserRow, theme.schedule.employeeAlloc],
  );
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const updateScrollArrows = useCallback(() => {
    const el = railScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);
  useEffect(() => {
    const el = railScrollRef.current;
    if (!el) return;
    updateScrollArrows();
    el.addEventListener('scroll', updateScrollArrows, { passive: true });
    const ro = new ResizeObserver(updateScrollArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollArrows);
      ro.disconnect();
    };
  }, [updateScrollArrows, tasks.length]);
  // Smooth, EASED week paging — the native scrollBy({behavior:'smooth'}) felt like an
  // abrupt snap on a one-week jump, so tween scrollLeft over ~420ms with an ease-in-out
  // curve via requestAnimationFrame. scrollAnimRef cancels any in-flight tween so rapid
  // clicks don't fight each other.
  const scrollAnimRef = useRef<number | null>(null);
  const scrollRailByWeek = useCallback((dir: -1 | 1) => {
    const el = railScrollRef.current;
    if (!el) return;
    if (scrollAnimRef.current !== null) cancelAnimationFrame(scrollAnimRef.current);
    const startLeft = el.scrollLeft;
    const maxLeft = el.scrollWidth - el.clientWidth;
    const endLeft = Math.max(0, Math.min(maxLeft, startLeft + dir * RAIL_WEEK_W));
    const delta = endLeft - startLeft;
    if (delta === 0) return;
    const duration = 420;
    const startTime = performance.now();
    const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
    const step = (now: number) => {
      const t = Math.min(1, (now - startTime) / duration);
      el.scrollLeft = startLeft + delta * easeInOut(t);
      scrollAnimRef.current = t < 1 ? requestAnimationFrame(step) : null;
    };
    scrollAnimRef.current = requestAnimationFrame(step);
  }, []);
  useEffect(() => () => {
    if (scrollAnimRef.current !== null) cancelAnimationFrame(scrollAnimRef.current);
  }, []);

  // BST week columns for the shared sticky rail header (same span both grids compute).
  const railWeekColumns = useMemo(() => {
    let minStart: string | null = null;
    let maxEnd: string | null = null;
    for (const t of tasks) {
      if (t.StartDate && (!minStart || t.StartDate < minStart)) minStart = t.StartDate;
      if (t.EndDate && (!maxEnd || t.EndDate > maxEnd)) maxEnd = t.EndDate;
    }
    return minStart && maxEnd ? weekStartsInRange(minStart, maxEnd) : [];
  }, [tasks]);

  const handleToggleBulkMode = () => {
    setBulkMode((v) => !v);
    setSelectedKeys(new Set());
  };

  const handleToggleRow = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedKeys(
      selectedKeys.size === tasks.length
        ? new Set()
        : new Set(tasks.map((t) => t.ScheduleTaskKey)),
    );
  };

  /**
   * 6C.5 — Live capacity recompute.
   * Incrementing this key causes Phase2CapacityTable (table 3) to re-fetch
   * from /schedule/employee-weeks.  We debounce the increment so rapid
   * workload-bar drags settle to a single refetch ≈300 ms after release.
   */
  const [capacityRefreshKey, setCapacityRefreshKey] = useState(0);
  const capacityDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const notifyAllocationChanged = useCallback(() => {
    if (capacityDebounceTimer.current) clearTimeout(capacityDebounceTimer.current);
    capacityDebounceTimer.current = setTimeout(() => {
      setCapacityRefreshKey((prev) => prev + 1);
      // Tell the live Machine + Employee Schedules a scenario allocation/booking
      // changed so they quietly revalidate too (cross-view live refresh).
      emitScheduleChange({ kind: 'assignments-changed', source: 'scenario-planning' });
    }, 300);
  }, []);

  // ── Data loading (tasks only — assignments owned by useTaskAssignments) ───

  const loadTasks = useCallback(async () => {
    if (!source || !sourceKey) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listTasksForSource(source, sourceKey, scenarioKey ?? undefined);
      setTasks(data);
      // Assignments are automatically fetched by useTaskAssignments when tasks change.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks.');
    } finally {
      setLoading(false);
    }
  }, [source, sourceKey, scenarioKey]);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  // Cross-view live refresh: a booking/assignment change in the live Machine or
  // Employee Schedule quietly re-loads the scenario tasks (so machine bars + dates
  // reflect the move) and bumps the capacity refetch. Ignore our OWN echo. loadTasks
  // keeps the grids mounted (the spinner only shows on an empty first load).
  const loadTasksRef = useRef(loadTasks);
  useEffect(() => { loadTasksRef.current = loadTasks; }, [loadTasks]);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const off = onScheduleChange((change) => {
      if (change.source === 'scenario-planning') return; // our own echo
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void loadTasksRef.current();
        notifyAllocationChanged();
      }, 300);
    });
    return () => { if (timer) clearTimeout(timer); off(); };
  }, [notifyAllocationChanged]);

  /**
   * 6D.2 — On-enter refresh.
   * Re-fetches tasks + (via useTaskAssignments) assignments whenever the
   * Phase-2 panel is (re-)activated (onEnterRefreshKey incremented by
   * ScenarioPhaseStepper on phase transition to 2). Skip value 0 to avoid
   * doubling the initial mount fetch that [loadTasks] already performs.
   */
  const onEnterRefreshKeyRef = useRef(0);
  useEffect(() => {
    if (onEnterRefreshKey === 0) return;            // skip initial mount
    if (onEnterRefreshKey === onEnterRefreshKeyRef.current) return; // no-op duplicate
    onEnterRefreshKeyRef.current = onEnterRefreshKey;
    // Belt-and-suspenders: entering Phase 2 starts with a clean availability cache so
    // the first assignee-dropdown open re-fetches fresh load for the (possibly moved)
    // booking dates — even if a Phase-1 emit was somehow missed (Gigi, 2026-06-22).
    clearCapacityCache();
    void loadTasks();
    notifyAllocationChanged();
  }, [onEnterRefreshKey, loadTasks, notifyAllocationChanged]);

  // ── Per-task assignee refresh (calls through to the shared hook) ──────────

  /**
   * Wrapper so both Phase2TaskGrid and the dialog callbacks have a stable
   * function reference. Calls reloadOneAssignment (from useTaskAssignments)
   * which updates the shared assignmentMap — reflected in BOTH panels at once.
   */
  const reloadOneTaskAssignees = useCallback(
    (taskKey: string) => reloadOneAssignment(taskKey),
    [reloadOneAssignment],
  );

  // ── Dialog wiring (escalation from InlineAssigneePicker) ─────────────────

  const handleEscalateToDialog = useCallback(
    (task: TaskWithBookings, preselectUser?: AssignableUser) => {
      setDialogTask(task);
      setDialogPreselect(preselectUser ?? null);
    },
    [],
  );

  const handleCloseDialog = useCallback(() => {
    setDialogTask(null);
    setDialogPreselect(null);
  }, []);

  const handleDialogSaved = useCallback(
    (count: number) => {
      if (dialogTask) {
        // Reload shared assignment map — both panels see the new person instantly.
        void reloadOneTaskAssignees(dialogTask.ScheduleTaskKey);
      }
      // Dismiss success — chip refresh is enough feedback.
      void count; // count used to match onSaved signature
      // Assignments changed via dialog — capacity table needs a refetch (6C.5).
      notifyAllocationChanged();
    },
    [dialogTask, reloadOneTaskAssignees, notifyAllocationChanged],
  );

  const handleDialogAssigneesChanged = useCallback(() => {
    if (dialogTask) {
      // Reload shared assignment map — both panels see the removal instantly.
      void reloadOneTaskAssignees(dialogTask.ScheduleTaskKey);
    }
    // Assignments changed via dialog — capacity table needs a refetch (6C.5).
    notifyAllocationChanged();
  }, [dialogTask, reloadOneTaskAssignees, notifyAllocationChanged]);

  // ── No-scenario guard ─────────────────────────────────────────────────────

  if (!scenario || !source || !sourceKey) {
    return (
      <Box
        sx={{
          mt: 2,
          p: 5,
          borderRadius: 2,
          border: '2px dashed',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <GroupsIcon sx={{ fontSize: '2.5rem', color: 'text.disabled' }} />
        <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
          Employee planning
        </Typography>
        <Typography variant="body2" color="text.disabled" textAlign="center">
          Select or create a scenario above to start planning employee assignments.
        </Typography>
      </Box>
    );
  }

  // A scheduled (Submitted) / archived plan is locked — staffing edits are refused
  // server-side (scenarioEditGuard), so grey the assign surface out and explain why.
  const locked = scenario.Status === 'Submitted' || scenario.Status === 'Archived';
  const lockedBodySx = locked
    ? { opacity: 0.55, pointerEvents: 'none' as const, filter: 'grayscale(0.25)' }
    : {};

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <FlashUserContext.Provider value={handleFlashUser}>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>

      {/* Read-only banner when the plan is scheduled. (The view toggle below stays usable.) */}
      {locked && (
        <Alert
          severity="info"
          icon={<LockOutlinedIcon fontSize="inherit" />}
          sx={{ '& .MuiAlert-message': { fontSize: '0.82rem' } }}
        >
          This plan is <strong>{scenario.Status === 'Archived' ? 'archived' : 'scheduled'}</strong> — staffing is read-only.
          Use <strong>Reopen to Edit</strong> (top of the page) to make changes, or <strong>Duplicate</strong> to plan a variation.
        </Alert>
      )}

      {/* ── Interactive working area — the Task Weekly Schedule + Employee
          Weekly Capacity (where you actually assign) sit ABOVE the read-only
          Project Testing Grid so you don't scroll past read-only content to work
          (Gigi, 2026-06-20). ── */}
      {/* Slim dismissible tip banner (designer pass 2026-07-26) — the view
          switcher moved up onto the phase-tab row, so a dismissed tip leaves
          NO row behind. */}
      {(tasks.length > 0 || hasIndexTests) && !tipDismissed && (
        <Alert
          severity="info"
          onClose={dismissTip}
          sx={{
            py: 0, px: 1.25,
            '& .MuiAlert-message': { fontSize: '0.74rem', py: 0.5 },
            '& .MuiAlert-icon': { fontSize: 16, py: 0.6, mr: 0.75 },
            '& .MuiAlert-action': { py: 0, pt: 0.25 },
          }}
        >
          Tip: L1/L2 employees require a supervisor (L3+) on the same task. Default allocation is 100%.
        </Alert>
      )}

      {/* ── "Staff this job" hero (PHASE2-REFRAME.md) — the job-centric people×
          weeks surface. Replaces the legacy task grid + capacity stack + the
          read-only Project Testing Grid below. ── */}
      {showStaffHero && (
        <StaffThisJobView
          scenario={scenario}
          source={source}
          sourceKey={sourceKey}
          scenarioKey={scenarioKey}
          tasks={tasks}
          assignmentMap={assignmentMap}
          locked={locked}
          onAllocationChanged={notifyAllocationChanged}
          onTaskPresenceChanged={reloadOneTaskAssignees}
          capacityRefreshKey={capacityRefreshKey}
          holidays={holidayDates}
          hoveredWeek={hoveredWeek}
          onHoverWeek={setHoveredWeek}
          onSwitchToClassic={() => onViewModeChange('classic')}
        />
      )}

      {/* ── Standalone "Roster" mode (Mike simplify PR2) — the SAME shared
          people×weeks roster timeline the Staff hero uses, rendered on its own
          (no task-staffing cards). One roster-timeline component, two entry
          points — always identical, consistent data + editing. ── */}
      {tasks.length > 0 && viewMode === 'roster' && (
        <StaffRosterView
          scenario={scenario}
          source={source}
          sourceKey={sourceKey}
          scenarioKey={scenarioKey}
          tasks={tasks}
          assignmentMap={assignmentMap}
          locked={locked}
          onAllocationChanged={notifyAllocationChanged}
          onTaskPresenceChanged={reloadOneTaskAssignees}
          capacityRefreshKey={capacityRefreshKey}
          hoveredWeek={hoveredWeek}
          onHoverWeek={setHoveredWeek}
          holidays={holidayDates}
        />
      )}

      {tasks.length > 0 && showLegacyRail && (
        /* Relative wrapper so the floating week-scroll arrows can sit over the rail's
           left/right edges and stay centred in view while you scroll (2026-06-23). */
        <Box sx={{ position: 'relative' }}>
        {/* ONE horizontal scroll container for BOTH grids — they stay locked together and
           the sticky name columns pin on the left. Scrolls with the PAGE vertically (no
           inner scroll region → no extra scrollbars, per Gigi 2026-06-23). The native
           horizontal scrollbar is fully hidden; the floating ◀/▶ arrows + trackpad drive
           sideways. (A truly-sticky week header would need an inner scroll region, which
           adds the scrollbars the user rejected — so the merged header rides with the
           page instead.) */}
        <Box
          ref={railScrollRef}
          sx={{
            overflowX: 'auto',
            maxWidth: '100%',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start', width: 'fit-content' }}>
          {/* Shared sticky WEEK + day header — one header for BOTH grids, pinned at the
              top while you scroll a tall plan (2026-06-23). */}
          <Phase2RailHeader
            weekColumns={railWeekColumns}
            holidays={holidayDates}
            hoveredWeek={hoveredWeek}
            onHoverWeek={setHoveredWeek}
          />
          {/* Task × Week weekly allocation grid (6C.2) — ALWAYS stacked above the
              capacity grid on a shared, week-aligned rail (2026-06-23). Greyed +
              non-interactive when the plan is scheduled (locked). */}
          <Box sx={{ width: 'fit-content', ...lockedBodySx }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5 }}>
              <PrecisionManufacturingIcon sx={{ fontSize: 18, color: theme.schedule.machineTime }} />
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
                Task Weekly Schedule (Machine Time + Employee Allocation)
              </Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {viewMode === 'board' ? (
              <Phase2BoardGrid
                tasks={tasks}
                assignmentMap={assignmentMap}
                scenarioKey={scenarioKey}
                onAssignmentsChanged={reloadOneTaskAssignees}
                onAllocationChanged={notifyAllocationChanged}
                compareToBaseline={compareToBaseline}
                hoveredWeek={hoveredWeek}
                onHoverWeek={setHoveredWeek}
                holidays={holidayDates}
              />
            ) : (
              <Phase2WeeklyGrid
                tasks={tasks}
                assignmentMap={assignmentMap}
                scenarioKey={scenarioKey}
                onAssignmentsChanged={reloadOneTaskAssignees}
                onAllocationChanged={notifyAllocationChanged}
                compareToBaseline={compareToBaseline}
                hoveredWeek={hoveredWeek}
                onHoverWeek={setHoveredWeek}
                holidays={holidayDates}
              />
            )}
          </Box>
          {/* Team Allocation & Weekly Capacity (staffing-allocation merge 2026-07-13):
              set each person's weekly allocation % right where you see the
              resulting under/at/over utilization band. Replaces the old separate
              Team Allocation panel — the tables now line up + share the week beam. */}
          <Box sx={{ width: 'fit-content' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5, flexWrap: 'wrap' }}>
              <GroupsIcon sx={{ fontSize: 18, color: theme.schedule.employeeAlloc }} />
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
                Team Allocation &amp; Weekly Capacity
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                Set how much of each person&rsquo;s week this {source === 'Quote' ? 'quote' : 'work order'} takes. Over 100% is allowed (advisory).
              </Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
            <Phase2CapacityTable
              tasks={tasks}
              taskAssignmentMap={assignmentMap}
              scenarioKey={scenarioKey}
              source={source}
              sourceKey={sourceKey}
              disabled={locked}
              onAllocationChanged={notifyAllocationChanged}
              refreshKey={capacityRefreshKey}
              holidays={holidayDates}
              hoveredWeek={hoveredWeek}
              onHoverWeek={setHoveredWeek}
            />
          </Box>
        </Box>
        </Box>

        {/* Floating week-scroll arrows — page the weeks without hunting for the scrollbar. */}
        <Phase2RailScrollArrows
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
          onScroll={scrollRailByWeek}
        />
        </Box>
      )}

      {/* ── Section 1 header — read-only Project Testing Grid (reference, below).
          Hidden in the Staff view (the hero doesn't show the read-only grid). ─ */}
      {showLegacyRail && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5 }}>
        <GroupsIcon sx={{ fontSize: 18, color: theme.schedule.employeeAlloc }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
          Project Testing Grid (Define Plan)
        </Typography>
        <Box sx={{ flex: 1 }} />

        {/* 6F.4 — Multi-select toggle (matches the mockup header toggle) */}
        {tasks.length > 0 && (
          <Button
            size="small"
            variant={bulkMode ? 'contained' : 'outlined'}
            startIcon={<EditIcon fontSize="small" />}
            onClick={handleToggleBulkMode}
            sx={{ textTransform: 'none', fontSize: '0.78rem' }}
          >
            Multi-select
          </Button>
        )}

        {/* Refresh */}
        <Tooltip title="Refresh tasks and assignees">
          <span>
            <Button
              size="small"
              startIcon={
                loading
                  ? <CircularProgress size={14} />
                  : <RefreshIcon fontSize="small" />
              }
              onClick={() => { void loadTasks(); }}
              disabled={loading}
              sx={{ fontSize: '0.78rem' }}
            >
              Refresh
            </Button>
          </span>
        </Tooltip>
      </Box>
      )}

      {showLegacyRail && <Divider />}

      {/* ── Error banner ───────────────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Loading spinner ────────────────────────────────────────────────── */}
      {loading && tasks.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* ── Empty state — suppressed when the Staff hero is rendering (it shows
          index/effort allocation rows even with zero machine tasks). For the
          legacy views, an index-only plan gets pointed at its Index Tests
          section below instead of the misleading "go back to Phase 1"
          (index-only fix, 2026-08-04). ── */}
      {!loading && tasks.length === 0 && !error && !showStaffHero && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            gap: 1.5,
          }}
        >
          <PrecisionManufacturingIcon
            sx={{ fontSize: 48, opacity: 0.3, color: theme.schedule.machineTime }}
          />
          <Typography variant="body1" fontWeight={500} color="text.secondary">
            {hasIndexTests ? 'No machine tasks' : 'No tasks found'}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', maxWidth: 380 }}
          >
            {hasIndexTests
              ? 'This plan only has index tests — staff them in the Index Tests section below, or switch to the Staff this job view.'
              : 'Go back to Phase 1 to add and assign machines to tasks.'}
          </Typography>
        </Box>
      )}

      {/* ── Phase-2 read-only task grid (6C.1 / 6F.4) — its assignee chips are editable,
          so grey it out too when the plan is locked. Hidden in the Staff/Roster
          hero views. ── */}
      {tasks.length > 0 && showLegacyRail && (
        <Box sx={lockedBodySx}>
          <Phase2TaskGrid
            tasks={tasks}
            assigneesByTask={assignmentMap}
            scenarioKey={scenarioKey}
            onReloadAssignees={reloadOneTaskAssignees}
            onAllocationChanged={notifyAllocationChanged}
            onEscalateToDialog={handleEscalateToDialog}
            bulkMode={bulkMode}
            selectedKeys={selectedKeys}
            onToggleSelect={handleToggleRow}
            onSelectAll={handleSelectAll}
            holidays={holidayDates}
          />
        </Box>
      )}

      {/* ── 6F.4 Bulk assign employee bar ─────────────────────────────────── */}
      {bulkMode && selectedKeys.size > 0 && (
        <Phase2BulkActionBar
          selectedTasks={tasks.filter((t) => selectedKeys.has(t.ScheduleTaskKey))}
          scenarioKey={scenarioKey}
          onDone={() => {
            setSelectedKeys(new Set());
            void loadTasks();
            notifyAllocationChanged();
          }}
        />
      )}

      {/* (Task Weekly Schedule + Employee Weekly Capacity now render at the TOP,
          above this read-only Project Testing Grid.) */}

      {/* ── Index Tests staffing (Item 9.4, R2) — pooled IndexAllocation rows for
          this source (hidden entirely when the source has no index tests).
          2026-07-28: in the default Staff view the allocations render INSIDE
          "Tests on this job" (IndexStaffingTableRows), so this bottom section
          only serves the legacy Classic / Board / Roster views. ── */}
      {viewMode !== 'staff' && (
        <IndexStaffingSection
          scenario={scenario}
          locked={locked}
          onEnterRefreshKey={onEnterRefreshKey}
          onAllocationChanged={notifyAllocationChanged}
        />
      )}

      {/* ── AssignEmployeesDialog (skill-gate / overbook escalation) ─────── */}
      <AssignEmployeesDialog
        open={dialogTask !== null}
        task={dialogTask}
        preselectUser={dialogPreselect}
        onClose={handleCloseDialog}
        onSaved={handleDialogSaved}
        onAssigneesChanged={handleDialogAssigneesChanged}
        scenarioKey={scenarioKey ?? undefined}
      />
    </Box>
    </FlashUserContext.Provider>
  );
};
